/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { LexicalEditor } from "lexical";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { TextNode } from "lexical";
import { useEffect } from "react";

import { CustomFieldKey, CustomFields } from "entities/domain/templates";

import {
  $createCustomFieldNode,
  CustomFieldNode,
} from "../nodes/CustomFieldNode";

function findAndTransformCustomField(
  node: TextNode,
  customFields: CustomFields,
  highlightUnknownCustomFields: boolean,
  openEditCustomFieldModal?: (key: string, value: string) => void
): null | TextNode {
  const text = node.getTextContent();
  const findCustomField = /{([a-zA-Z_-]+)}/g;
  const match = findCustomField.exec(text);

  if (!match) {
    return null;
  }

  const customFieldKey = match[1] as CustomFieldKey;

  const startsAt = match.index;
  let endsAt = startsAt + 1;
  const charactersFromText = text.split("");

  while (endsAt < charactersFromText.length) {
    const letter = charactersFromText[endsAt];

    if (/[\W]/.test(letter)) break;

    endsAt++;
  }

  let targetNode;

  if (startsAt === 0) {
    [targetNode] = node.splitText(startsAt + customFieldKey.length + 2);
  } else {
    [, targetNode] = node.splitText(
      startsAt,
      startsAt + customFieldKey.length + 2
    );
  }

  const customFieldNode = $createCustomFieldNode(
    customFields[customFieldKey],
    `{${customFieldKey}}`,
    highlightUnknownCustomFields,
    openEditCustomFieldModal
  );

  targetNode.replace(customFieldNode);

  return customFieldNode;
}

function useCustomFields(
  editor: LexicalEditor,
  customFields: CustomFields,
  highlightUnknownCustomFields: boolean,
  openEditCustomFieldModal?: (key: string, value: string) => void
): void {
  function textNodeTransform(node: TextNode): void {
    let targetNode: TextNode | null = node;

    while (targetNode !== null) {
      if (!targetNode.isSimpleText()) {
        return;
      }

      targetNode = findAndTransformCustomField(
        targetNode,
        customFields,
        highlightUnknownCustomFields,
        openEditCustomFieldModal
      );
    }
  }

  useEffect(() => {
    if (!editor.hasNodes([CustomFieldNode])) {
      throw new Error(
        "Custom Fields Plugin: CustomFieldNode not registered on editor"
      );
    }

    return editor.registerNodeTransform(TextNode, textNodeTransform);
  }, [editor, customFields]);
}

export default function CustomFieldShortcutPlugin({
  customFields,
  highlightUnknownCustomFields,
  openEditCustomFieldModal,
}: {
  customFields: CustomFields;
  highlightUnknownCustomFields: boolean;
  openEditCustomFieldModal?: (key: string, value: string) => void;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  useCustomFields(
    editor,
    customFields,
    highlightUnknownCustomFields,
    openEditCustomFieldModal
  );
  return null;
}
